/**
 * POSBatchServiceModal - Batch room service request from POS Dashboard
 *
 * Flow: Select rooms → Choose date preference → Submit batch request
 * Calls pos-request-service edge function.
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  POS_GOLD,
  POS_GOLD_DARK,
  calculateRoomStatus,
} from '../../../config/posRoomStatus';

interface Room {
  id: string;
  room_number: string;
  floor: string | null;
  bed_count: number;
  room_type: string;
  last_service_date: string | null;
  status: string;
}

type DatePreference = 'specific' | 'range' | 'flexible';

interface POSBatchServiceModalProps {
  propertyId: string;
  propertyName: string;
  hospitalityTier: string;
  onClose: () => void;
  onSuccess: () => void;
}

function POSBatchServiceModal({ propertyId, propertyName, hospitalityTier, onClose, onSuccess }: POSBatchServiceModalProps) {
  const { supabase } = useAuth();

  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Selection state
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(new Set());
  const [step, setStep] = useState<'rooms' | 'schedule'>('rooms');

  // Filters
  const [floorFilter, setFloorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Schedule form
  const [datePreference, setDatePreference] = useState<DatePreference>('specific');
  const [requestedDate, setRequestedDate] = useState('');
  const [requestedDateEnd, setRequestedDateEnd] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [notes, setNotes] = useState('');

  // Load rooms
  useEffect(() => {
    if (!supabase || !propertyId) return;

    async function loadRooms() {
      const { data, error: err } = await supabase!
        .from('hosp_rooms')
        .select('id, room_number, floor, bed_count, room_type, last_service_date, status')
        .eq('property_id', propertyId)
        .eq('status', 'active')
        .order('room_number');

      if (err) {
        setError('Failed to load rooms');
      } else {
        setRooms((data || []) as Room[]);
      }
      setLoading(false);
    }

    loadRooms();
  }, [supabase, propertyId]);

  // Calculate room service status — shared utility
  const getRoomStatus = (room: Room): string => {
    return calculateRoomStatus(room.last_service_date, hospitalityTier);
  };

  // Unique floors for filter
  const floors = useMemo(() => {
    const set = new Set(rooms.map((r) => r.floor || 'Unknown'));
    return Array.from(set).sort();
  }, [rooms]);

  // Filtered rooms
  const filteredRooms = useMemo(() => {
    return rooms.filter((r) => {
      if (floorFilter !== 'all' && (r.floor || 'Unknown') !== floorFilter) return false;
      if (statusFilter !== 'all' && getRoomStatus(r) !== statusFilter) return false;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getRoomStatus depends only on hospitalityTier which is in deps
  }, [rooms, floorFilter, statusFilter, hospitalityTier]);

  // Selection helpers
  const toggleRoom = (id: string) => {
    setSelectedRoomIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedRoomIds(new Set(filteredRooms.map((r) => r.id)));
  };

  const clearSelection = () => {
    setSelectedRoomIds(new Set());
  };

  // Summary
  const selectedRooms = rooms.filter((r) => selectedRoomIds.has(r.id));
  const totalBeds = selectedRooms.reduce((sum, r) => sum + (r.bed_count || 1), 0);

  // Submit
  const handleSubmit = async () => {
    if (selectedRoomIds.size === 0 || !requestedDate) return;

    setSubmitting(true);
    setError('');

    try {
      const { data: session } = await supabase!.auth.getSession();
      const token = session?.session?.access_token;

      const body: Record<string, unknown> = {
        property_id: propertyId,
        room_ids: Array.from(selectedRoomIds),
        requested_date: requestedDate,
        time_slot: timeSlot || null,
        notes: notes || null,
        scheduling_preference: datePreference,
      };

      if (datePreference !== 'specific' && requestedDateEnd) {
        body.requested_date_end = requestedDateEnd;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/pos-request-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create service request');
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const statusColors: Record<string, { label: string; bg: string; color: string }> = {
    'certified': { label: 'Certified', bg: 'var(--color-success-bg)', color: 'var(--color-success)' },
    'due-soon': { label: 'Due Soon', bg: 'var(--color-warning-bg)', color: 'var(--color-warning)' },
    'overdue': { label: 'Overdue', bg: 'var(--color-error-bg)', color: 'var(--color-error)' },
    'not-serviced': { label: 'Not Serviced', bg: 'var(--color-border)', color: 'var(--color-text-light)' },
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        {/* Header */}
        <div className="modal-header" style={{ background: `linear-gradient(135deg, ${POS_GOLD} 0%, ${POS_GOLD_DARK} 100%)`, color: '#fff' }}>
          <h2 className="modal-title">
            {step === 'rooms' ? 'Select Rooms' : 'Schedule Service'}
          </h2>
          <button className="modal-close" onClick={onClose} style={{ color: 'var(--color-white)', background: 'rgba(255,255,255,0.2)' }}>&times;</button>
        </div>

        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {step === 'rooms' ? (
            <>
              {/* Filters */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <select
                  value={floorFilter}
                  onChange={(e) => setFloorFilter(e.target.value)}
                  className="form-control"
                  style={{ flex: 1 }}
                >
                  <option value="all">All Floors</option>
                  {floors.map((f) => (
                    <option key={f} value={f}>Floor {f}</option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-control"
                  style={{ flex: 1 }}
                >
                  <option value="all">All Statuses</option>
                  <option value="overdue">Overdue</option>
                  <option value="due-soon">Due Soon</option>
                  <option value="not-serviced">Not Serviced</option>
                  <option value="certified">Certified</option>
                </select>
              </div>

              {/* Select All / Clear */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-light)' }}>
                  {filteredRooms.length} rooms shown
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={selectAll} style={{ background: 'none', border: 'none', fontSize: 13, color: POS_GOLD, cursor: 'pointer', fontWeight: 600 }}>
                    Select All
                  </button>
                  {selectedRoomIds.size > 0 && (
                    <button onClick={clearSelection} style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--color-text-light)', cursor: 'pointer' }}>
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Room list */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-light)' }}>Loading rooms...</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {filteredRooms.map((room) => {
                    const isSelected = selectedRoomIds.has(room.id);
                    const roomStatus = getRoomStatus(room);
                    const sc = statusColors[roomStatus] ?? { label: 'Unknown', bg: 'var(--color-border)', color: 'var(--color-text-light)' };

                    return (
                      <label
                        key={room.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 12px', borderRadius: 6, cursor: 'pointer',
                          background: isSelected ? 'rgba(189, 152, 47, 0.08)' : 'transparent',
                          border: `1px solid ${isSelected ? POS_GOLD : 'transparent'}`,
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRoom(room.id)}
                          style={{ accentColor: POS_GOLD }}
                        />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>Room {room.room_number}</span>
                          {room.floor && <span style={{ fontSize: 12, color: 'var(--color-text-light)', marginLeft: 8 }}>Floor {room.floor}</span>}
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--color-text-light)' }}>{room.bed_count} bed{room.bed_count !== 1 ? 's' : ''}</span>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                          background: sc.bg, color: sc.color,
                        }}>
                          {sc.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            /* Schedule Step */
            <>
              <div style={{ background: 'var(--color-bg)', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                  {selectedRoomIds.size} room{selectedRoomIds.size !== 1 ? 's' : ''} selected
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-light)' }}>
                  {totalBeds} bed{totalBeds !== 1 ? 's' : ''} at {propertyName}
                </div>
              </div>

              {/* Date Preference */}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label" style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Date Preference</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { value: 'specific' as DatePreference, label: 'Specific Date' },
                    { value: 'range' as DatePreference, label: 'Date Range' },
                    { value: 'flexible' as DatePreference, label: 'Flexible' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setDatePreference(opt.value)}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                        border: `1px solid ${datePreference === opt.value ? POS_GOLD : '#e5e7eb'}`,
                        background: datePreference === opt.value ? 'rgba(189, 152, 47, 0.1)' : 'var(--color-white)',
                        color: datePreference === opt.value ? POS_GOLD : 'var(--color-text-light)',
                        cursor: 'pointer',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date inputs */}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label" style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                  {datePreference === 'specific' ? 'Date' : 'From'}
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={requestedDate}
                  onChange={(e) => setRequestedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {datePreference !== 'specific' && (
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label" style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={requestedDateEnd}
                    onChange={(e) => setRequestedDateEnd(e.target.value)}
                    min={requestedDate || new Date().toISOString().split('T')[0]}
                  />
                  {datePreference === 'flexible' && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      {[
                        { label: 'This Week', days: 7 - new Date().getDay() },
                        { label: 'Next Week', days: 14 - new Date().getDay() },
                        { label: 'This Month', days: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate() - new Date().getDate() },
                      ].map((preset) => {
                        const start = new Date();
                        const end = new Date();
                        end.setDate(end.getDate() + preset.days);
                        const startStr = start.toISOString().split('T')[0] || '';
                        const endStr = end.toISOString().split('T')[0] || '';
                        return (
                          <button
                            key={preset.label}
                            onClick={() => { setRequestedDate(startStr); setRequestedDateEnd(endStr); }}
                            style={{
                              padding: '4px 10px', borderRadius: 4, fontSize: 12,
                              border: '1px solid #e5e7eb', background: 'var(--color-bg)', color: 'var(--color-text-light)',
                              cursor: 'pointer',
                            }}
                          >
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Time preference */}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label" style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Time Preference</label>
                <select className="form-control" value={timeSlot} onChange={(e) => setTimeSlot(e.target.value)}>
                  <option value="">Flexible</option>
                  <option value="Morning">Morning</option>
                  <option value="Afternoon">Afternoon</option>
                  <option value="Full Day">Full Day</option>
                </select>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label" style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Notes</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special instructions..."
                />
              </div>
            </>
          )}

          {error && (
            <div className="alert alert-danger mt-md">
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {step === 'rooms' ? (
            <>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: POS_GOLD, border: 'none' }}
                disabled={selectedRoomIds.size === 0}
                onClick={() => setStep('schedule')}
              >
                Next: Schedule ({selectedRoomIds.size} room{selectedRoomIds.size !== 1 ? 's' : ''})
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setStep('rooms')}>Back</button>
              <button
                className="btn btn-primary"
                style={{ background: POS_GOLD, border: 'none' }}
                disabled={!requestedDate || submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Submitting...' : `Request Service (${selectedRoomIds.size} rooms, ${totalBeds} beds)`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default POSBatchServiceModal;
